/**
 * AdminConfig Context
 *
 * Provides admin configuration (admin users, contractors) loaded from the API.
 * Used to control access to admin features like Reports, Calendar tech selector, etc.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { getAdminConfig, updateAdminUsers, ContractorConfig } from '../services/api';

interface AdminConfigContextType {
  adminEmails: string[];
  contractors: ContractorConfig[];
  hiddenResourceIds: number[];
  isLoading: boolean;
  error: string | null;
  isAdmin: (email: string | undefined) => boolean;
  addAdmin: (email: string) => Promise<void>;
  removeAdmin: (email: string) => Promise<void>;
  refresh: () => Promise<void>;
}

// Default admin emails (fallback if API fails)
const DEFAULT_ADMIN_EMAILS = ['jgoode@umetech.net', 'ken@umetech.net'];

const AdminConfigContext = createContext<AdminConfigContextType | undefined>(undefined);

export function AdminConfigProvider({ children }: { children: ReactNode }) {
  const [adminEmails, setAdminEmails] = useState<string[]>(DEFAULT_ADMIN_EMAILS);
  const [contractors, setContractors] = useState<ContractorConfig[]>([]);
  const [hiddenResourceIds, setHiddenResourceIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const config = await getAdminConfig();
      setAdminEmails(config.admins || DEFAULT_ADMIN_EMAILS);
      setContractors(config.contractors || []);
      setHiddenResourceIds(config.hiddenResourceIds || []);
    } catch (err) {
      console.error('Failed to load admin config:', err);
      setError(err instanceof Error ? err.message : 'Failed to load admin config');
      // Keep defaults on error
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const isAdmin = useCallback((email: string | undefined): boolean => {
    if (!email) return false;
    const normalized = email.toLowerCase().trim();

    // Embed mode users are always admin — they accessed through authenticated HUD
    if (normalized === 'embed@hud.local' || normalized.includes('hud.local')) {
      return true;
    }

    return adminEmails.some(admin => admin.toLowerCase() === normalized);
  }, [adminEmails]);

  const addAdmin = useCallback(async (email: string) => {
    const normalized = email.toLowerCase().trim();
    if (adminEmails.some(a => a.toLowerCase() === normalized)) {
      throw new Error('Email already in admin list');
    }
    const newList = [...adminEmails, normalized];
    await updateAdminUsers(newList);
    setAdminEmails(newList);
  }, [adminEmails]);

  const removeAdmin = useCallback(async (email: string) => {
    const normalized = email.toLowerCase().trim();
    const newList = adminEmails.filter(a => a.toLowerCase() !== normalized);
    if (newList.length === 0) {
      throw new Error('Cannot remove the last admin');
    }
    await updateAdminUsers(newList);
    setAdminEmails(newList);
  }, [adminEmails]);

  return (
    <AdminConfigContext.Provider value={{
      adminEmails,
      contractors,
      hiddenResourceIds,
      isLoading,
      error,
      isAdmin,
      addAdmin,
      removeAdmin,
      refresh: loadConfig,
    }}>
      {children}
    </AdminConfigContext.Provider>
  );
}

export function useAdminConfig() {
  const context = useContext(AdminConfigContext);
  if (context === undefined) {
    throw new Error('useAdminConfig must be used within an AdminConfigProvider');
  }
  return context;
}
